import React from 'react';

// Consistent label + control + error/hint wrapper used by every form in the
// app, so validation messaging and spacing don't have to be reinvented per page.
export const FormField = ({ label, htmlFor, required, error, hint, children, className = '' }) => (
  <div className={className}>
    {label && (
      <label htmlFor={htmlFor} className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1.5">
        {label}
        {required && <span className="text-rose-500 ml-0.5" aria-label="required">*</span>}
      </label>
    )}
    {children}
    {error && (
      <p className="text-rose-500 dark:text-rose-400 text-xs mt-1.5 flex items-center gap-1.5 font-medium" role="alert">
        <i className="fas fa-exclamation-circle"></i>{error}
      </p>
    )}
    {!error && hint && <p className="text-slate-400 dark:text-slate-500 text-xs mt-1.5">{hint}</p>}
  </div>
);

// A titled group of fields, used to break long forms into logical sections
// (e.g. "Basic Info", "Roles & Permissions", "Password") per the enterprise
// form-layout convention: related fields grouped, not one long flat list.
export const FormSection = ({ title, icon, description, children, className = '' }) => (
  <div className={`border border-slate-200 dark:border-slate-700 rounded-xl sm:rounded-2xl p-4 sm:p-6 ${className}`}>
    {title && (
      <div className="mb-4">
        <h3 className="text-base sm:text-lg font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
          {icon && <i className={`fas ${icon} text-primary dark:text-accent`} aria-hidden="true"></i>}
          {title}
        </h3>
        {description && <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{description}</p>}
      </div>
    )}
    <div className="space-y-4">{children}</div>
  </div>
);

const baseControlClass = 'w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/15 dark:focus:ring-accent/20 focus:border-primary dark:focus:border-accent transition-all disabled:bg-slate-50 dark:disabled:bg-slate-800 disabled:text-slate-400 disabled:cursor-not-allowed shadow-sm hover:border-slate-300 dark:hover:border-slate-500';

export const Input = React.forwardRef(({ error, className = '', ...rest }, ref) => (
  <input
    ref={ref}
    className={`${baseControlClass} ${error ? 'border-rose-300 dark:border-rose-500/50 focus:border-rose-500 focus:ring-rose-500/15' : ''} ${className}`}
    {...rest}
  />
));

export const Select = React.forwardRef(({ error, className = '', children, ...rest }, ref) => (
  <select
    ref={ref}
    className={`${baseControlClass} appearance-none cursor-pointer ${error ? 'border-rose-300 dark:border-rose-500/50 focus:border-rose-500 focus:ring-rose-500/15' : ''} ${className}`}
    {...rest}
  >
    {children}
  </select>
));

export const Textarea = React.forwardRef(({ error, className = '', ...rest }, ref) => (
  <textarea
    ref={ref}
    className={`${baseControlClass} resize-y ${error ? 'border-rose-300 dark:border-rose-500/50 focus:border-rose-500 focus:ring-rose-500/15' : ''} ${className}`}
    {...rest}
  />
));

export default FormField;
